import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { flow, isEmpty, uniqBy } from 'lodash';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildMakeModelDefaultYear,
  buildMmyModelFamilyIdsPath,
  buildModelFamilyIdsModelYearsPath,
  VehicleModel,
  VehicleEntities,
  buildForSaleYearsPath,
} from 'client/data/models/vehicle-v2';
import {
  getFilteredModelYears,
  getModelYearsWithReview,
  getModelYearsForSale,
  getForSaleLinks,
} from 'site-modules/shared/utils/core-page/model-year-helpers';
import { SpeculationRule } from 'site-modules/shared/components/speculation-rule/speculation-rule';
import { YearLinksList } from './year-links-list/year-links-list';

import './other-years-core-6811.scss';

const FOR_SALE_LINKS_NUM = 5;

// TODO: Consider moving to utils after core-6811
// TODO: Consider renaming noFollow after core-6811
export function reviewLinksFilter({ noFollow }) {
  return !noFollow;
}
export function forSaleLinksFilter(_, index) {
  return index < FOR_SALE_LINKS_NUM;
}

function OtherYearsCore6811UI({
  vehicle,
  modelYears,
  forSaleYears,
  defaultYear,
  className,
  isMobile,
  hasSpeculationRule,
}) {
  const containerRef = useRef();

  const {
    year,
    model: { name: modelName, slug: modelSlug },
    make: { name: makeName, slug: makeSlug },
  } = vehicle;
  const filteredModelYears = useMemo(() => getFilteredModelYears(modelYears, modelName), [modelYears, modelName]);

  if (isEmpty(filteredModelYears)) {
    return null;
  }

  const allModelYears = getModelYearsWithReview(filteredModelYears, defaultYear);
  const yearsForSale = uniqBy(
    [...getForSaleLinks({ forSaleYears, makeSlug, modelSlug }), ...getModelYearsForSale(filteredModelYears)],
    'name'
  );

  return (
    <div
      className={classnames('other-years-core-6811', className, { 'd-none': allModelYears.length < 2 })}
      data-tracking-parent="years-module"
      ref={containerRef}
    >
      <div className="mb-0_5">See other review years</div>
      <YearLinksList
        className="review-years medium"
        years={allModelYears}
        isMobile={isMobile}
        linkTrackingId="see_other_years_core"
        a11yMoreLabel={`${makeName} ${modelName} model review years`}
        currentYear={year}
        firstLinksFilter={reviewLinksFilter}
      />
      <div className="mt-1_5 mb-1 heading-6">
        {makeName} {modelName} for Sale
      </div>
      <YearLinksList
        className="medium"
        years={yearsForSale}
        isMobile={isMobile}
        linkTrackingId="see_other_years_srp"
        a11yMoreLabel={`${makeName} ${modelName} model for sale years`}
        firstLinksFilter={forSaleLinksFilter}
      />

      {hasSpeculationRule && (
        <SpeculationRule
          observeRef={containerRef}
          selector=".other-years-core-6811 .review-years a"
          notSelector="[rel~=nofollow]"
        />
      )}
    </div>
  );
}

OtherYearsCore6811UI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  modelYears: PropTypes.arrayOf(VehicleEntities.ModelYearByFamilyId),
  forSaleYears: PropTypes.arrayOf(PropTypes.number),
  defaultYear: PropTypes.number,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
  hasSpeculationRule: PropTypes.bool,
};

OtherYearsCore6811UI.defaultProps = {
  vehicle: null,
  modelYears: null,
  forSaleYears: null,
  defaultYear: null,
  className: null,
  isMobile: false,
  hasSpeculationRule: false,
};

export const OtherYearsCore6811 = flow(
  component =>
    connectToModel(component, {
      defaultYear: bindToPath(
        ({ vehicle }) => buildMakeModelDefaultYear({ make: vehicle.make.slug, model: vehicle.model.slug }),
        VehicleModel
      ),
      modelYears: bindToPath(
        ({ modelFamilyIds }) => !isEmpty(modelFamilyIds) && buildModelFamilyIdsModelYearsPath(modelFamilyIds),
        VehicleModel
      ),
    }),
  component =>
    connectToModel(component, {
      modelFamilyIds: bindToPath(
        ({ vehicle }) =>
          buildMmyModelFamilyIdsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
        VehicleModel
      ),
      forSaleYears: bindToPath(
        ({ vehicle }) =>
          buildForSaleYearsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
        VehicleModel
      ),
    })
)(OtherYearsCore6811UI);
